import React, { FC, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from '@emotion/styled';
import css from '@styled-system/css';
import shouldForwardProp from '@styled-system/should-forward-prop';
import themeGet from '@styled-system/theme-get';
import * as PropTypes from 'prop-types';
import { variant } from 'styled-system';

import { useResizeObserver } from '../../hooks/useResizeObserver';
import { StyleFn, WithDivProps } from '../../internal/styles/types';
import { getVariantProp } from '../../internal/styles/utils';
import {
  Direction,
  PopoverContainerProps,
  PopoverProps,
  StyledPopoverProps,
} from './types';

// TODO: implement click away (optional: production one does not have it)
const MAP_PLACEMENT_ALIGN_TO_TRANSFORM_ORIGIN = {
  top: {
    start: 'bottom left',
    end: 'bottom right',
    middle: 'bottom center',
    none: 'bottom',
  },
  bottom: {
    start: 'top left',
    end: 'top right',
    middle: 'top center',
    none: 'top',
  },
  right: {
    start: 'top left',
    end: 'bottom left',
    middle: 'left center',
    none: 'left',
  },
  left: {
    start: 'top right',
    end: 'bottom right',
    middle: 'right center',
    none: 'right',
  },
};

const getIsRtl = (direction) => direction === 'rtl';

const getPhysicalPositionFromLogicPosition = (
  placement: StyledPopoverProps['placement'],
  direction: StyledPopoverProps['documentDir']
) => {
  const isRtl = getIsRtl(direction);

  if (placement === 'inline-start') {
    return isRtl ? 'right' : 'left';
  }

  if (placement === 'inline-end') {
    return isRtl ? 'left' : 'right';
  }

  return placement || 'bottom';
};

const computedTransformOrigin: StyleFn<StyledPopoverProps> = ({
  align = 'none',
  placement = 'top',
  documentDir,
}) => {
  const physicalPlacement = getPhysicalPositionFromLogicPosition(
    placement,
    documentDir
  );

  return css({
    transformOrigin:
      MAP_PLACEMENT_ALIGN_TO_TRANSFORM_ORIGIN[physicalPlacement][align],
  });
};

const StyledPopover = styled('div', {
  shouldForwardProp: (prop) => shouldForwardProp(prop) && prop !== 'open',
})<StyledPopoverProps>(
  css({
    position: 'absolute',
    borderRadius: 1,
    boxShadow: '0',
    maxHeight: '90vh',
    transition: 'transform .2s,opacity .2s,visibility .2s',
    zIndex: 'popover',
    willChange: 'transform, opacity',
    '::before': {
      position: 'absolute',
      zIndex: -2,
      border: '1px solid transparent',
      boxShadow: '0',
      bottom: '100%',
      transform: 'translateY(50%) rotate(45deg)',
      content: '""',
    },
    '::after': {
      position: 'absolute',
      zIndex: -1,
      top: 0,
      left: 1,
      right: 1,
      content: '""',
    },
  }),
  ({ size, documentDir }) => {
    const isRtl = getIsRtl(documentDir);

    const rightPlacementStyle = {
      left: '100%',
      ml: `popover.${size}.value`,
      '::before': {
        boxShadow: '0',
        left: '0',
        transform: 'translateX(-50%) rotate(45deg)',
      },
      '::after': {
        left: 0,
        right: 'unset',
      },
    };

    const leftPlacementStyle = {
      right: '100%',
      mr: `popover.${size}.value`,
      '::before': {
        boxShadow: '0',
        right: '0',
        transform: 'translateX(50%) rotate(-45deg)',
      },
      '::after': {
        right: 0,
        left: 'unset',
      },
    };

    return getVariantProp({
      prop: 'placement',
      variants: {
        top: {
          bottom: '100%',
          mt: 0,
          mb: `popover.${size}.value`,
          '::before': {
            boxShadow: '0',
            bottom: '0',
            transform: 'translateY(50%) rotate(-45deg)',
          },
          '::after': {
            bottom: 0,
            top: 'unset',
          },
        },
        bottom: {
          top: '100%',
          mt: `popover.${size}.value`,
          mb: 0,
        },
        'inline-end': isRtl ? leftPlacementStyle : rightPlacementStyle,
        'inline-start': isRtl ? rightPlacementStyle : leftPlacementStyle,
      },
    });
  },
  ({ placement, documentDir, size, theme }) => {
    const physicalPlacement = getPhysicalPositionFromLogicPosition(
      placement,
      documentDir
    );

    // Horizontal alignment
    if (['top', 'bottom'].includes(physicalPlacement)) {
      return getVariantProp({
        prop: 'align',
        variants: {
          start: {
            left: `popover.${size}.halfInverted`,
            '::before': {
              left: `popover.arrow.${size}.value`,
            },
          },
          end: {
            right: `popover.${size}.halfInverted`,
            '::before': {
              right: `popover.arrow.${size}.value`,
            },
          },
          middle: {
            left: '50%',
            transform: `translate(-50%)`,
            '::before': {
              left: `calc(50% - ${themeGet(`space.popover.arrow.${size}.half`)({
                theme,
              })}px)`,
            },
          },
          none: {},
        },
      });
    }

    // Vertical alignment
    if (['right', 'left'].includes(physicalPlacement)) {
      return getVariantProp({
        prop: 'align',
        variants: {
          start: {
            top: `popover.${size}.halfInverted`,
            '::before': {
              top: `popover.arrow.${size}.value`,
            },
          },
          end: {
            bottom: `popover.${size}.halfInverted`,
            '::before': {
              bottom: `popover.arrow.${size}.value`,
            },
          },
          middle: {
            top: `calc(50% - var(--popover-height-half))`,
            ...(physicalPlacement === 'right'
              ? { left: '100%' }
              : { right: '100%' }),
            '::before': {
              top: `calc(50% - ${themeGet(`space.popover.arrow.${size}.half`)({
                theme,
              })}px - 1px)`,
            },
          },
          none: {
            top: '0',
            '::before': {
              top: `popover.arrow.${size}.value`,
            },
          },
        },
      });
    }
  },
  ({ placement, documentDir }) => {
    const physicalPlacement = getPhysicalPositionFromLogicPosition(
      placement,
      documentDir
    );

    if (['top', 'bottom'].includes(physicalPlacement)) {
      return getVariantProp({
        prop: 'size',
        variants: {
          expanded: {
            minWidth: '132px',
            maxWidth: '632px',
            width: 'max-content',
            padding: 4,
            '::before': {
              width: 'popover.arrow.expanded.value',
              height: 'popover.arrow.expanded.value',
            },
            '::after': {
              height: 'popover.arrow.expanded.value',
            },
          },
          compact: {
            minWidth: '56px',
            width: 'max-content',
            padding: 2,
            '::before': {
              width: 'popover.arrow.compact.value',
              height: 'popover.arrow.compact.value',
            },
            '::after': {
              height: 'popover.arrow.compact.value',
            },
          },
          tiny: {
            minWidth: 'popover.tiny',
            '::before': {
              width: 'popover.arrow.tiny.value',
              height: 'popover.arrow.tiny.value',
            },
            '::after': {
              height: 'popover.arrow.tiny.value',
            },
          },
          small: {
            minWidth: 'popover.small',
            '::before': {
              width: 'popover.arrow.small.value',
              height: 'popover.arrow.small.value',
            },
            '::after': {
              height: 'popover.arrow.small.value',
            },
          },
          medium: {
            minWidth: 'popover.medium',
            '::before': {
              width: 'popover.arrow.medium.value',
              height: 'popover.arrow.medium.value',
            },
            '::after': {
              height: 'popover.arrow.medium.value',
            },
          },
          large: {
            minWidth: 'popover.large',
            '::before': {
              width: 'popover.arrow.large.value',
              height: 'popover.arrow.large.value',
            },
            '::after': {
              height: 'popover.arrow.large.value',
            },
          },
        },
      });
    }

    if (['right', 'left'].includes(physicalPlacement)) {
      return getVariantProp({
        prop: 'size',
        variants: {
          expanded: {
            minWidth: '132px',
            maxWidth: '632px',
            width: 'max-content',
            padding: 4,
            '::before': {
              width: 'popover.arrow.expanded.value',
              height: 'popover.arrow.expanded.value',
            },
            '::after': {
              width: 'popover.arrow.expanded.value',
              height: 'calc(100% - 8px)',
              top: '4px',
            },
          },
          compact: {
            minWidth: '56px',
            width: 'max-content',
            padding: 2,
            '::before': {
              width: 'popover.arrow.compact.value',
              height: 'popover.arrow.compact.value',
            },
            '::after': {
              width: 'popover.arrow.compact.value',
              height: 'calc(100% - 8px)',
              top: '4px',
            },
          },
          tiny: {
            minWidth: 'popover.tiny',
            '::before': {
              width: 'popover.arrow.tiny.value',
              height: 'popover.arrow.tiny.value',
            },
            '::after': {
              width: 'popover.arrow.tiny.value',
              height: 'calc(100% - 8px)',
              top: '4px',
            },
          },
          small: {
            minWidth: 'popover.small',
            '::before': {
              width: 'popover.arrow.small.value',
              height: 'popover.arrow.small.value',
            },
            '::after': {
              width: 'popover.arrow.small.value',
              height: 'calc(100% - 8px)',
              top: '4px',
            },
          },
          medium: {
            minWidth: 'popover.medium',
            '::before': {
              width: 'popover.arrow.medium.value',
              height: 'popover.arrow.medium.value',
            },
            '::after': {
              width: 'popover.arrow.medium.value',
              height: 'calc(100% - 8px)',
              top: '4px',
            },
          },
          large: {
            minWidth: 'popover.large',
            '::before': {
              width: 'popover.arrow.large.value',
              height: 'popover.arrow.large.value',
            },
            '::after': {
              width: 'popover.arrow.large.value',
              height: 'calc(100% - 8px)',
              top: '4px',
            },
          },
        },
      });
    }
  },
  ({ placement = 'bottom' }) => {
    const marginsByPlacement = {
      default: {
        top: {
          mb: '16px',
        },
        bottom: {
          mt: '16px',
        },
        'inline-end': {
          'margin-inline-start': '16px',
        },
        'inline-start': {
          'margin-inline-end': '16px',
        },
      },
    };

    return getVariantProp({
      prop: 'distance',
      variants: {
        default: marginsByPlacement.default[placement as string],
        none: {
          mt: 0,
          mb: 0,
          ml: 0,
          mr: 0,
        },
      },
    });
  },
  getVariantProp({
    prop: 'type',
    variants: {
      default: {
        color: 'neutral.90',
        backgroundColor: 'white',
        '::before': {
          backgroundColor: 'white',
        },
        '::after': {
          backgroundColor: 'white',
        },
      },
      positive: {
        color: 'white',
        backgroundColor: 'positive.default',
        '::before': {
          backgroundColor: 'positive.default',
        },
        '::after': {
          backgroundColor: 'positive.default',
        },
      },
      warning: {
        color: 'white',
        backgroundColor: 'warning.default',
        '::before': {
          backgroundColor: 'warning.default',
        },
        '::after': {
          backgroundColor: 'warning.default',
        },
      },
      informative: {
        color: 'white',
        backgroundColor: 'informative.default',
        '::before': {
          backgroundColor: 'informative.default',
        },
        '::after': {
          backgroundColor: 'informative.default',
        },
      },
      critical: {
        color: 'white',
        backgroundColor: 'negative.default',
        '::before': {
          backgroundColor: 'negative.default',
        },
        '::after': {
          backgroundColor: 'negative.default',
        },
      },
      primePrimary: {
        color: 'white',
        backgroundColor: 'primary.90',
        '::before': {
          backgroundColor: 'primary.90',
        },
        '::after': {
          backgroundColor: 'primary.90',
        },
      },
      primeSecondary: {
        color: 'primary.90',
        backgroundColor: 'secondary.50',
        '::before': {
          backgroundColor: 'secondary.50',
        },
        '::after': {
          backgroundColor: 'secondary.50',
        },
      },
    },
  }),
  ({ size = 'expanded' }) => {
    if (['expanded', 'compact'].includes(size)) {
      return;
    }

    return getVariantProp({
      prop: 'padding',
      variants: {
        fit: {
          padding: 2,
        },
        normal: {
          padding: 4,
        },
        none: {
          padding: 0,
        },
      },
    });
  },
  ({ placement, documentDir, align, open }) => {
    const physicalPlacement = getPhysicalPositionFromLogicPosition(
      placement,
      documentDir
    );

    const translateForMiddleAlignment = ['top', 'bottom'].includes(
      physicalPlacement
    )
      ? '-50%'
      : '0%';

    return open
      ? css({
          visibility: 'visible',
          opacity: 1,
          transform: `translate(${
            align === 'middle' ? translateForMiddleAlignment : '0%'
          }) scale(1)`,
        })
      : css({
          visibility: 'hidden',
          opacity: 0,
          transform: `translate(${
            align === 'middle' ? translateForMiddleAlignment : '0%'
          }) scale(0.7)`,
        });
  },
  computedTransformOrigin
);

export const PopoverContainer = styled('div', {
  shouldForwardProp,
})<PopoverContainerProps>(
  {
    position: 'relative',
  },
  variant({
    prop: 'size',
    variants: {
      full: {
        width: '100%',
      },
    },
  })
);

function isValidDirection(dir: string): dir is Direction {
  return ['ltr', 'rtl'].includes(dir);
}

const updateDirection = (documentDir, setDocumentDir) => {
  const newDir = document.documentElement.dir;

  if (newDir === documentDir) {
    return;
  }

  if (isValidDirection(newDir)) {
    setDocumentDir(newDir);
  }

  // For dir="auto", an incorrect or a missing value
  setDocumentDir(
    window.getComputedStyle(document.documentElement).direction as Direction
  );
};

export const Popover: FC<WithDivProps<PopoverProps>> = ({
  size = 'small',
  placement = 'bottom',
  align = 'start',
  padding = 'normal',
  type = 'default',
  ...props
}) => {
  const popoverRef = useRef<HTMLDivElement>(null);
  const [documentDir, setDocumentDir] =
    useState<StyledPopoverProps['documentDir']>('ltr');

  useEffect(() => {
    updateDirection(documentDir, setDocumentDir);

    const observer = new MutationObserver(() => {
      updateDirection(documentDir, setDocumentDir);
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['dir'],
    });

    return () => {
      observer.disconnect();
    };
  }, [documentDir]);

  const {
    borderBoxSize: { blockSize: popoverHeight },
  } = useResizeObserver(popoverRef);

  useLayoutEffect(() => {
    if (props.open) {
      popoverRef.current?.style.setProperty(
        '--popover-height-half',
        `${popoverHeight / 2}px`
      );
    }
  }, [props.open, popoverHeight]);

  return (
    <StyledPopover
      ref={popoverRef}
      size={size}
      placement={placement}
      align={align}
      padding={padding}
      type={type}
      {...props}
      documentDir={documentDir}
      aria-hidden={!props.open}
      role="tooltip"
    />
  );
};

Popover.propTypes = {
  open: PropTypes.bool.isRequired,
  placement: PropTypes.oneOf(['bottom', 'top', 'inline-end', 'inline-start']),
  align: PropTypes.oneOf(['start', 'end', 'middle', 'none']),
  size: PropTypes.oneOf([
    'expanded',
    'compact',
    'tiny',
    'small',
    'medium',
    'large',
  ]),
  distance: PropTypes.oneOf(['default', 'none']),
  padding: PropTypes.oneOf(['normal', 'fit', 'none']),
  type: PropTypes.oneOf([
    'default',
    'positive',
    'warning',
    'informative',
    'critical',
    'primePrimary',
    'primeSecondary',
  ]),
};

export default Popover;
